import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updateProfile, changePassword } from '../api';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [position, setPosition] = useState(user?.position || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileMsg('');
    setProfileError('');
    try {
      await updateProfile({ first_name: firstName, last_name: lastName, position });
      setProfileMsg('Profile updated successfully!');
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Could not update profile.');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    setPasswordError('');
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordMsg('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Could not change password.');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      {/* Navbar */}
      <nav style={styles.nav}>
        <div style={styles.navBrand}>
          <span style={styles.navTitle}>Boothby Therapy Services</span>
          <span style={styles.navSubtitle}>Employee Learning Hub</span>
        </div>
        <div style={styles.navRight}>
          <button onClick={() => navigate('/dashboard')} style={styles.navBtn}>
            📚 Resources
          </button>
          <button onClick={handleLogout} style={styles.navBtnOutline}>
            Sign Out
          </button>
        </div>
      </nav>

      <div style={styles.content}>
        <h1 style={styles.pageTitle}>My Profile</h1>

        {/* Profile Info */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Personal Information</h2>
          <form onSubmit={handleProfileUpdate} style={styles.form}>
            {profileMsg && <div style={styles.success}>{profileMsg}</div>}
            {profileError && <div style={styles.error}>{profileError}</div>}
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>First Name</label>
                <input
                  style={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Last Name</label>
                <input
                  style={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email Address</label>
              <input style={{...styles.input, background: '#f9fafb'}} value={user?.email} disabled />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Position / Role</label>
              <input
                style={styles.input}
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Occupational Therapist"
              />
            </div>
            <button type="submit" style={styles.button}>Save Changes</button>
          </form>
        </div>

        {/* Change Password */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Change Password</h2>
          <form onSubmit={handlePasswordChange} style={styles.form}>
            {passwordMsg && <div style={styles.success}>{passwordMsg}</div>}
            {passwordError && <div style={styles.error}>{passwordError}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Current Password</label>
              <input
                type="password"
                style={styles.input}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>New Password</label>
              <input
                type="password"
                style={styles.input}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" style={styles.button}>Change Password</button>
          </form>
        </div>

        {/* Future: Certificates */}
        <div style={{...styles.card, borderStyle: 'dashed', background: '#f8fafc'}}>
          <h2 style={{...styles.cardTitle, color: '#9ca3af'}}>📜 Certificates & Documents</h2>
          <p style={{color: '#9ca3af', fontSize: '14px'}}>
            Certificate uploads and documents will be available in a future phase.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', overflowX: 'hidden' },
  nav: { background: '#243C94', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  navBrand: { display: 'flex', flexDirection: 'column' },
  navTitle: { color: '#ffffff', fontSize: '16px', fontWeight: '700' },
  navSubtitle: { color: '#FFA204', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' },
  navRight: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  navBtn: { background: 'rgba(255,255,255,0.15)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  navBtnOutline: { background: 'transparent', color: '#ffffff', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  content: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px' },
  pageTitle: { color: '#243C94', fontSize: '24px', fontWeight: '700', marginBottom: '24px' },
  card: { background: '#ffffff', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
  cardTitle: { color: '#1f2937', fontSize: '18px', fontWeight: '700', marginBottom: '24px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '140px' },
  label: { color: '#374151', fontSize: '14px', fontWeight: '600' },
  input: { padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  button: { background: '#FFA204', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start' },
  success: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '12px', borderRadius: '8px', fontSize: '14px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px' },
};